import { Tooltip, Text } from "@mantine/core";
import { DataTable, Badge, StatusBadge } from "@xplatform/design-system";
import type { DataTableColumn } from "@xplatform/design-system";
import { useTranslation } from "react-i18next";
import type { MonitoringMappingJobViewModel } from "@models/monitoring";
import { formatCurrency, formatDate } from "@utils/formatters";
import "./MappingJobTable.scss";

interface MappingJobTableProps {
    jobs: MonitoringMappingJobViewModel[];
    onRowClick: (job: MonitoringMappingJobViewModel) => void;
}

const formatRows = (rowsDone: number | null, rowsTotal: number | null): string => {
    if (rowsDone === null && rowsTotal === null) return "-";
    return `${rowsDone ?? 0}/${rowsTotal ?? 0}`;
};

export const MappingJobTable = ({ jobs, onRowClick }: MappingJobTableProps) => {
    const { t } = useTranslation();

    const columns: DataTableColumn<MonitoringMappingJobViewModel>[] = [
        {
            key: "jobId",
            header: t("admin.monitoring.labels.jobId"),
            width: "8%",
            render: (job) => (
                <Tooltip label={job.jobId} position="top">
                    <Text size="sm" className="mapping-job-table__id">
                        {job.jobIdShort}
                    </Text>
                </Tooltip>
            ),
        },
        {
            key: "status",
            header: t("admin.monitoring.labels.status"),
            width: "12%",
            render: (job) => (
                <StatusBadge size="sm" status={job.status}>
                    {job.status}
                </StatusBadge>
            ),
        },
        {
            key: "plugin",
            header: t("admin.monitoring.labels.plugin"),
            width: "12%",
            render: (job) => (
                <Text size="sm" c="dimmed" truncate="end">
                    {job.pluginId || "-"}
                </Text>
            ),
        },
        {
            key: "rows",
            header: t("admin.monitoring.labels.rows"),
            width: "8%",
            align: "right",
            render: (job) => (
                <Badge size="sm" variant="outline" color="gray">
                    {formatRows(job.rowsDone, job.rowsTotal)}
                </Badge>
            ),
        },
        {
            key: "totalCost",
            header: t("admin.monitoring.labels.totalCost"),
            width: "12%",
            align: "right",
            render: (job) => (
                <Text size="sm" fw={600}>
                    {formatCurrency(job.totalCost, job.currency)}
                </Text>
            ),
        },
        {
            key: "events",
            header: t("admin.monitoring.labels.events"),
            width: "8%",
            align: "right",
            render: (job) => <Text size="sm">{job.eventCount.toLocaleString()}</Text>,
        },
        {
            key: "createdBy",
            header: t("admin.monitoring.labels.createdBy"),
            width: "14%",
            render: (job) => (
                <Text size="sm" c="dimmed" truncate="end">
                    {job.createdBy || "-"}
                </Text>
            ),
        },
        {
            key: "organisation",
            header: t("admin.monitoring.labels.organisation"),
            width: "14%",
            render: (job) => (
                <Text size="sm" c="dimmed" truncate="end">
                    {job.organisationName || "-"}
                </Text>
            ),
        },
        {
            key: "created",
            header: t("admin.monitoring.labels.created"),
            render: (job) => (
                <Text size="sm" c="dimmed">
                    {formatDate(job.createdAt)}
                </Text>
            ),
        },
    ];

    return (
        <DataTable<MonitoringMappingJobViewModel>
            className="mapping-job-table"
            fz="sm"
            verticalSpacing="xs"
            horizontalSpacing="md"
            columns={columns}
            data={jobs}
            getRowKey={(job) => job.jobId}
            onRowClick={(job) => onRowClick(job)}
        />
    );
};
